import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, tokens } from '../api/client';
import { keys } from '../api/hooks';
import type { AuthSession, EmailCodeRequest, SmsCodeRequest } from '../api/types';
import { TopBar } from '../components/chrome';
import { Cta, LinkButton, Muted, Note, Screen, Title } from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { useOtpCooldown } from '../lib/use-otp-cooldown';

type LoginMethod = 'email' | 'sms';

/** Email or SMS one-time code. Browsing lots and prices needs no account at all. */
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' }) as { next?: string };
  const [method, setMethod] = useState<LoginMethod>('email');
  const [phase, setPhase] = useState<'identity' | 'code'>('identity');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestInFlight = useRef(false);
  const otpCooldown = useOtpCooldown();

  const identityValid = method === 'email' ? email.includes('@') : phone.trim().length >= 7;
  const codeValid = method === 'email' ? /^\d{6}$/.test(code) : /^\d{4,10}$/.test(code);
  const target = method === 'email' ? email : phone;

  const finish = () => {
    if (search.next) window.location.replace(search.next);
    else void navigate({ to: '/' });
  };

  const requestCode = async () => {
    if (requestInFlight.current || !identityValid || (phase === 'code' && !otpCooldown.canResend)) return;
    requestInFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      if (method === 'email') {
        const result = await api.post<EmailCodeRequest>('/auth/otp/request', {
          email,
          locale: i18n.language.startsWith('zh') ? 'zh' : 'en',
        });
        setEmail(result.email);
        setDevCode(result.devCode ?? null);
        otpCooldown.start(result.resendAfterSeconds);
      } else {
        const result = await api.post<SmsCodeRequest>('/auth/sms/request', { phone });
        setPhone(result.phoneE164);
        setDevCode(null);
        otpCooldown.start(result.resendAfterSeconds);
      }
      setCode('');
      setPhase('code');
    } catch (e) {
      setError(describeError(e, 'login.sendFailed'));
    } finally {
      requestInFlight.current = false;
      setBusy(false);
    }
  };

  const verify = async () => {
    if (requestInFlight.current || phase !== 'code' || !codeValid) return;
    requestInFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const session =
        method === 'email'
          ? await api.post<AuthSession>('/auth/otp/verify', { email, code })
          : await api.post<AuthSession>('/auth/sms/verify', { phone, code });
      tokens.save(session);
      queryClient.clear();
      queryClient.setQueryData(keys.currentUser, session.user);
      finish();
    } catch (e) {
      setError(describeError(e, 'login.verifyFailed'));
    } finally {
      requestInFlight.current = false;
      setBusy(false);
    }
  };

  const selectMethod = (next: LoginMethod) => {
    if (next === method || busy) return;
    setMethod(next);
    setCode('');
    setDevCode(null);
    setError(null);
    otpCooldown.reset();
  };

  return (
    <>
      <TopBar title={t('login.title')} />
      <Screen>
        <Title sub={t('login.brand')}>{t('common.brand')}</Title>

        {phase === 'identity' ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void requestCode();
            }}
          >
            <div
              role="tablist"
              aria-label={t('login.methodLabel')}
              className="grid grid-cols-2 rounded-lg bg-surface-muted p-[3px]"
            >
              {(['email', 'sms'] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={method === item}
                  disabled={busy}
                  onClick={() => selectMethod(item)}
                  className={`min-h-[46px] rounded-md px-3 text-sm font-medium ${
                    method === item ? 'bg-white text-ink shadow-sm' : 'text-muted'
                  }`}
                >
                  {t(`login.method.${item}`)}
                </button>
              ))}
            </div>

            {method === 'email' ? (
              <>
                <Muted>{t('login.emailLabel')}</Muted>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  disabled={busy}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  aria-label={t('login.emailLabel')}
                  className="min-h-[56px] rounded-lg border-[1.5px] border-ink px-4 text-lg outline-none"
                />
              </>
            ) : (
              <>
                <Muted>{t('login.phoneLabel')}</Muted>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  disabled={busy}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="(312) 555-0148"
                  aria-label={t('login.phoneLabel')}
                  className="num min-h-[56px] rounded-lg border-[1.5px] border-ink px-4 text-lg outline-none"
                />
                <Muted>{t('login.phoneHint')}</Muted>
              </>
            )}
            <Cta type="submit" label={t('login.sendCode')} loading={busy} disabled={!identityValid} />
          </form>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void verify();
            }}
          >
            <Muted>{t('login.codeSent', { target })}</Muted>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={busy}
              maxLength={method === 'email' ? 6 : 10}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              placeholder={t('login.codePlaceholder')}
              aria-label={t('login.codePlaceholder')}
              className="num min-h-[56px] rounded-lg border-[1.5px] border-ink px-4 text-xl tracking-[6px] outline-none"
            />
            {devCode ? <Muted>{t('login.devCode', { code: devCode })}</Muted> : null}
            <Muted>{t(method === 'email' ? 'login.emailCodeExpiry' : 'login.smsCodeExpiry')}</Muted>
            <Cta type="submit" label={t('login.signIn')} loading={busy} disabled={!codeValid} />
            <LinkButton
              label={
                otpCooldown.canResend
                  ? t('login.resendCode')
                  : t('login.resendIn', { seconds: otpCooldown.remainingSeconds })
              }
              disabled={busy || !otpCooldown.canResend}
              onClick={() => void requestCode()}
            />
            <LinkButton
              label={t(method === 'email' ? 'login.changeEmail' : 'login.changePhone')}
              disabled={busy}
              onClick={() => {
                otpCooldown.reset();
                setCode('');
                setDevCode(null);
                setError(null);
                setPhase('identity');
              }}
            />
          </form>
        )}

        {error ? <p className="text-[13px] text-danger">{error}</p> : null}

        <Note>{t('login.note')}</Note>
      </Screen>
    </>
  );
}
